import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router";

import "bootstrap/dist/css/bootstrap.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "@/utils/axiosInterceptor";

import App from "@/App.tsx";
import ScrollToTop from "@/ScrollToTop";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { PersistGate } from "redux-persist/integration/react";

import { setClientCurrencyDetails } from "@/redux/auth";
import store, { persistor } from "@/redux/store.ts";

import { resolveClientCurrency } from "@/utils/commonFunctions";

import "./sentry";
import "./index.scss";
import "@/assets/styles/main.scss";

const applyCurrencyFromUrl = () => {
  try {
    const search = window.location.search;
    const clientCode = new URLSearchParams(search).get("client_code");
    const currency = resolveClientCurrency(
      store.getState()?.auth,
      clientCode,
      search
    );
    if (currency) {
      store.dispatch(setClientCurrencyDetails(currency as any));
    }
  } catch {
    // Ignore; fall back to the rehydrated currency.
  }
};

// Eagerly load every component/screen stylesheet at app startup so styles are
// always present regardless of which lazy-loaded route chunk is active.
// Without this, a class defined in one lazy chunk's .scss is missing until that
// chunk loads (visible as broken UI in dev mode). This restores the pre-lazy
// behavior where all CSS was bundled together — JS stays code-split.
// Excluded: *.module.scss (locally scoped CSS Modules) and the design-system
// partials in assets/styles (already pulled in via main.scss above).
// import.meta.glob(
//   ["/src/**/*.scss", "!/src/**/*.module.scss", "!/src/assets/styles/**"],
//   { eager: true }
// );

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Provider store={store}>
      <PersistGate
        loading={null}
        persistor={persistor}
        onBeforeLift={applyCurrencyFromUrl}
      >
        <BrowserRouter>
          <ScrollToTop />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <App />
          </LocalizationProvider>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  </StrictMode>
);
